import {
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  Typography,
} from '@mui/material'
import { ReactElement, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Add, Reorder, Search } from '@mui/icons-material'
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd'
import type { DropResult } from '@hello-pangea/dnd'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import { useNotifications } from 'src/v3/manage/hooks/use-notifications'
import { usePageTour } from 'src/v3/tours/hooks/use-page-tour'
import {
  fetchLinkedServices,
  fetchSeries,
  selectLinkedServices,
  selectLinkedServicesStatus,
  selectSeries,
  selectSeriesStatus,
} from '../create.slice'
import { CREATE_SERIES_URLS } from '../constants'
import { deleteSeries, updateSeriesDisplayOrder } from '../utils/api'
import SeriesLineItem from './components/series-line-item'
import { SeriesResponse } from './types'

export default function CreateSeriesV5(): ReactElement {
  let dispatch = useAppDispatch()
  let navigate = useNavigate()
  let { showError, showSuccess } = useNotifications()

  usePageTour('series')

  let series = useAppSelector(selectSeries)
  let seriesStatus = useAppSelector(selectSeriesStatus)
  let linkedServices = useAppSelector(selectLinkedServices)
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)

  let [statusFilter, setStatusFilter] = useState('all')
  let [searchQuery, setSearchQuery] = useState('')
  let [editOrderSections, setEditOrderSections] = useState<Set<string>>(
    new Set(),
  )
  let [localOrder, setLocalOrder] = useState<Map<string, string[]>>(new Map())
  let [isSavingOrder, setIsSavingOrder] = useState(false)
  let [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  let [seriesToDelete, setSeriesToDelete] = useState<SeriesResponse | null>(
    null,
  )

  useAutoFetch(seriesStatus, fetchSeries)
  useAutoFetch(linkedServicesStatus, fetchLinkedServices)

  let filteredSeries = useMemo(
    () =>
      series.filter((s) => {
        if (statusFilter === 'published' && s.published !== 'published') {
          return false
        }
        if (statusFilter === 'draft' && s.published === 'published') {
          return false
        }
        if (searchQuery) {
          let query = searchQuery.toLowerCase()
          if (!s.name?.toLowerCase().includes(query)) return false
        }
        return true
      }),
    [series, statusFilter, searchQuery],
  )

  let groupedSeries = useMemo(() => {
    let groups = filteredSeries.reduce<Map<string | null, SeriesResponse[]>>(
      (acc, s) => {
        let key = s.linkedServiceID
        let existing = acc.get(key) ?? []
        return new Map(acc).set(key, [...existing, s])
      },
      new Map(),
    )
    return new Map(
      [...groups.entries()].map(([key, items]) => [
        key,
        items.toSorted((a, b) => a.sequence - b.sequence),
      ]),
    )
  }, [filteredSeries])

  let isFiltered = statusFilter !== 'all' || searchQuery !== ''

  let resetFilters = () => {
    setStatusFilter('all')
    setSearchQuery('')
  }

  let handleDeleteClick = (s: SeriesResponse) => {
    setSeriesToDelete(s)
    setDeleteDialogOpen(true)
  }

  let handleDeleteConfirm = () => {
    if (!seriesToDelete) return
    deleteSeries(seriesToDelete.id)
      .then(() => {
        dispatch(fetchSeries())
        setDeleteDialogOpen(false)
        setSeriesToDelete(null)
        showSuccess(`Successfully deleted series "${seriesToDelete.name}"`)
      })
      .catch(() => {
        showError(`Failed to delete series "${seriesToDelete?.name}"`)
      })
  }

  let handleDeleteCancel = () => {
    setDeleteDialogOpen(false)
    setSeriesToDelete(null)
  }

  let linkedServiceName = (id: string | null) =>
    linkedServices.find((ls) => ls.id === id)?.name

  let isLoading =
    seriesStatus === 'pending' ||
    seriesStatus === 'uninitialized' ||
    linkedServicesStatus === 'pending' ||
    linkedServicesStatus === 'uninitialized'

  let toggleEditOrder = (lsID: string) => {
    if (!editOrderSections.has(lsID)) {
      let currentItems = groupedSeries.get(lsID) ?? []
      setLocalOrder((prev) =>
        new Map(prev).set(
          lsID,
          currentItems.map((s) => s.id),
        ),
      )
      setEditOrderSections((prev) => new Set([...prev, lsID]))
    } else {
      setLocalOrder((prev) => {
        let next = new Map(prev)
        next.delete(lsID)
        return next
      })
      setEditOrderSections((prev) => {
        let next = new Set(prev)
        next.delete(lsID)
        return next
      })
    }
  }

  let handleDragEnd = (result: DropResult) => {
    if (!result.destination) return
    let lsID = result.source.droppableId
    let current = localOrder.get(lsID) ?? []
    let src = result.source.index
    let dst = result.destination.index
    let moved = current[src]
    let reordered = current.filter((_, i) => i !== src).toSpliced(dst, 0, moved)

    setLocalOrder((prev) => new Map(prev).set(lsID, reordered))

    let assignments = reordered.map((seriesID, i) => ({
      seriesID,
      sequence: i + 1,
    }))
    setIsSavingOrder(true)
    updateSeriesDisplayOrder(lsID, assignments)
      .catch(() => showError('Failed to save display order'))
      .finally(() => setIsSavingOrder(false))
  }

  return (
    <Container maxWidth="xl" className="py-6">
      <Box className="mb-6">
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          data-tour="series-index-header"
        >
          Series
        </Typography>
      </Box>

      <Box className="flex flex-wrap items-center justify-between gap-2 mb-6">
        <Button
          variant="contained"
          size="large"
          startIcon={<Add />}
          onClick={() => navigate(CREATE_SERIES_URLS.CREATE)}
          className="mb-2 md:mb-0 basis-full md:basis-auto"
          data-tour="series-create-button"
        >
          Create Series
        </Button>

        <Box className="flex items-center justify-end gap-2">
          <Box role="status" aria-live="polite" aria-atomic="true">
            <Chip
              size="medium"
              label={`${filteredSeries.length} series`}
              color="primary"
            />
          </Box>

          {isFiltered && (
            <Button
              size="medium"
              variant="outlined"
              color="secondary"
              onClick={resetFilters}
              className="whitespace-nowrap h-8"
            >
              Reset Filters
            </Button>
          )}
        </Box>
      </Box>

      <Box
        className="grid grid-cols-1 gap-6 mb-6 lg:grid-cols-2"
        data-tour="series-filters"
      >
        <FormControl sx={{ backgroundColor: 'background.paperDark' }}>
          <InputLabel id="status-filter">Status</InputLabel>
          <Select
            labelId="status-filter"
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            size="medium"
          >
            <MenuItem value="all">All Statuses</MenuItem>
            <MenuItem value="published">Published</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label="Search"
          placeholder="Search by name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="medium"
          sx={{ backgroundColor: 'background.paperDark' }}
          slotProps={{
            input: {
              startAdornment: <Search className="mr-2" aria-hidden="true" />,
            },
          }}
        />
      </Box>

      {isLoading ? (
        <Box className="py-16 text-center" role="status" aria-live="polite">
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            Loading series...
          </Typography>
        </Box>
      ) : filteredSeries.length === 0 ? (
        <Box className="py-16 text-center">
          <Typography
            variant="h3"
            component="h2"
            sx={{ fontSize: '1rem', fontWeight: 400, color: 'text.secondary' }}
          >
            {isFiltered ? 'No series match your filters' : 'No series found'}
          </Typography>
          {isFiltered && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={resetFilters}
              className="mt-4"
            >
              Clear Filters
            </Button>
          )}
        </Box>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          {[...groupedSeries.entries()].map(([lsID, items]) => {
            let sectionName = lsID
              ? (linkedServiceName(lsID) ?? lsID)
              : 'No Linked Service'
            let sectionEditMode = !!lsID && editOrderSections.has(lsID)
            let orderedItems = sectionEditMode
              ? (localOrder.get(lsID!) ?? [])
                  .map((id) => items.find((s) => s.id === id))
                  .filter((s): s is SeriesResponse => s !== undefined)
              : items

            return (
              <Box key={lsID ?? 'none'} className="mb-8">
                <Box className="flex items-center justify-between mb-3">
                  <Typography variant="h6" component="h2">
                    {sectionName}
                  </Typography>
                  {lsID && (
                    <ToggleButton
                      value="editOrder"
                      selected={sectionEditMode}
                      onChange={() => toggleEditOrder(lsID)}
                      size="small"
                      color="primary"
                      aria-label={`Edit order for ${sectionName}`}
                      disabled={isSavingOrder}
                      className="gap-2 px-3"
                      sx={{
                        color: 'primary.main',
                        borderColor: 'primary.main',
                      }}
                    >
                      <Reorder fontSize="small" />
                      Edit Order
                    </ToggleButton>
                  )}
                </Box>

                {sectionEditMode ? (
                  <Droppable droppableId={lsID!}>
                    {(provided) => (
                      <Box
                        className="flex flex-col gap-4"
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                      >
                        {orderedItems.map((s, index) => (
                          <Draggable
                            key={s.id}
                            draggableId={s.id}
                            index={index}
                            isDragDisabled={isSavingOrder}
                          >
                            {(prov, snapshot) => (
                              <Box
                                ref={prov.innerRef}
                                {...prov.draggableProps}
                                sx={{
                                  opacity:
                                    snapshot.isDragging || isSavingOrder
                                      ? 0.5
                                      : 1,
                                }}
                              >
                                <SeriesLineItem
                                  series={s}
                                  linkedServiceName={undefined}
                                  onDelete={handleDeleteClick}
                                  dragHandleProps={
                                    prov.dragHandleProps ?? undefined
                                  }
                                />
                              </Box>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </Box>
                    )}
                  </Droppable>
                ) : (
                  <Box
                    component="ul"
                    className="flex flex-col gap-4"
                    sx={{ listStyle: 'none', p: 0, m: 0 }}
                  >
                    {orderedItems.map((s) => (
                      <li key={s.id}>
                        <SeriesLineItem
                          series={s}
                          linkedServiceName={undefined}
                          onDelete={handleDeleteClick}
                        />
                      </li>
                    ))}
                  </Box>
                )}
              </Box>
            )
          })}
        </DragDropContext>
      )}

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-series-dialog-title"
      >
        <DialogTitle id="delete-series-dialog-title">Delete Series</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{seriesToDelete?.name}"? This
            action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}
